import type { ReactNode } from 'react';

import Link from 'next/link';

import type { SeriesClipViewModel } from '../types/SeriesClipViewModel';
import { SeriesClipCellView } from './SeriesClipCellView';

/** Default thumbnail resolution to request per clip cell. */
export const DEFAULT_CLIP_WIDTH = 120;
/** Clip rail shows 3 full cells + a thin partial 4th — the legacy My Life peek. */
const VISIBLE_CLIP_SLOTS = 3.2;
/** Matches the rail's `gap-2` (8px); 3 gaps sit within the visible slots. */
const CLIP_GAP_PX = 8;
/**
 * Responsive cell width: fit {@link VISIBLE_CLIP_SLOTS} slots (with their 3
 * inter-cell gaps) across the rail's content width so the 4th clip always peeks.
 * Pure CSS `%` of the flex container — no JS measurement.
 */
export const CLIP_CELL_WIDTH = `calc((100% - ${CLIP_GAP_PX * 3}px) / ${VISIBLE_CLIP_SLOTS})`;

interface Props {
  clips: SeriesClipViewModel[];
  /** Pixel resolution to request each clip thumbnail at (cell width stays responsive). */
  clipWidth?: number;
  /**
   * CSS width for each rail cell. Defaults to {@link CLIP_CELL_WIDTH} (My Life's
   * 3.2-slot, container-relative peek). A wider surface (the course detail) passes
   * a FIXED width instead, so more full cells fit per row as the rail widens —
   * and fewer on a narrow viewport — at a constant cell size.
   */
  cellWidth?: string;
  /**
   * Wrap one clip cell — the editable orchestrator uses this to inject a per-clip
   * owner menu (and its own link). When omitted, a clip with a `videoHref`
   * renders as a plain `<Link>` to the player (the visitor default); a clip
   * without one renders static.
   */
  renderClip?: (clip: SeriesClipViewModel, cell: ReactNode) => ReactNode;
  /** Resolved clip-title style (creator `font_config`), forwarded to each cell. */
  clipTitleStyle?: import('react').CSSProperties;
}

/**
 * The clip cells of a series/course rail — container-less (a fragment), so it
 * drops straight into the rail's scroll container as flex children. Shared by
 * the card's first page, its horizontal load-more batches, and the static
 * (Storybook / editable) rail, so every clip cell renders identically.
 */
export function SeriesClipCells({
  clips,
  clipWidth = DEFAULT_CLIP_WIDTH,
  cellWidth = CLIP_CELL_WIDTH,
  renderClip,
  clipTitleStyle,
}: Props) {
  return (
    <>
      {clips.map(clip => {
        const cell = (
          <SeriesClipCellView
            clip={clip}
            thumbnailWidth={clipWidth}
            titleStyle={clipTitleStyle}
          />
        );
        return (
          <div key={clip.id} className="shrink-0" style={{ width: cellWidth }}>
            {renderClip ? (
              renderClip(clip, cell)
            ) : clip.videoHref ? (
              <Link href={clip.videoHref} className="block">
                {cell}
              </Link>
            ) : (
              cell
            )}
          </div>
        );
      })}
    </>
  );
}
